import { apiClient, HttpError } from '@littlebird/auth-client';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import { userStore } from '@/stores/user.store';
import { trackEvent } from '@/utils/analytics';

import {
  NoteItemSchema,
  NotesStoreSchema,
  type NoteItem,
  type NotesStore,
} from './assistant-notes.types';

const ITEMS_URL = 'assistant_notes/items';

// The module shows every server message itself, so the global error toast stays off
const QUIET = { emitHttpErrorEvent: false };
// For the item writes: after a 5xx that came once the write had committed, a retried add would
// create the note twice, and a retried save or delete would send a version that is gone and be
// refused for the user's own write
const QUIET_NO_RETRIES = { ...QUIET, retryOptions: { retries: 0 } };

export const notesStoreQueryKey = () => [
  'assistant-notes',
  'store',
  userStore.userId,
];

export const fetchNotesStore = async (
  signal?: AbortSignal
): Promise<NotesStore> =>
  NotesStoreSchema.parse(await apiClient.get(ITEMS_URL, { ...QUIET, signal }));

export const notesStoreQueryOptions = () =>
  queryOptions({
    queryKey: notesStoreQueryKey(),
    queryFn: ({ signal }) => fetchNotesStore(signal),
    staleTime: 0,
    refetchOnMount: 'always',
    // A focus or reconnect refetch that flips the mode would drop a dirty text area without the
    // in-place message; the failed save is the one sanctioned switch while the user types
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // The API client retries 5xx responses only, so a dropped connection needs these
    retry: 3,
  });

export const useNotesStoreQuery = () => useQuery(notesStoreQueryOptions());

/** The whole-text save of a store kept as text, through the user endpoint as before items */
export const saveNotesText = (text: string) =>
  apiClient.post('update_user_data', { chat_derived_prompt: text }, QUIET);

const userErrorMessage = (error: unknown): string | undefined => {
  if (!(error instanceof HttpError)) return undefined;
  const message: unknown = error.body?.detail?.user_error_message;
  return typeof message === 'string' ? message : undefined;
};

/** Toasts the message the server wrote for the user, or the fallback when it sent none */
export const reportNoteError = (error: unknown, fallback: string) => {
  toast.error(userErrorMessage(error) ?? fallback);
};

type NoteWriteVariables = { number: number; text: string; version: number };

const useNoteWriteMutation = <TVariables, TResult>(
  request: (variables: TVariables) => Promise<TResult>,
  applyToItems: (
    items: NoteItem[],
    result: TResult,
    variables: TVariables
  ) => NoteItem[],
  failureMessage: string
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    // The account that sent the write. A write that returns after another account signed in
    // must not reach that account's notes or toasts
    onMutate: () => userStore.userId,
    onSuccess: async (result, variables, senderId) => {
      if (senderId !== userStore.userId) return;
      // A reload of the list still out, after a Discard or a refused write, would bring back
      // the list from before this write when it returns
      await queryClient.cancelQueries({ queryKey: notesStoreQueryKey() });
      queryClient.setQueryData<NotesStore>(notesStoreQueryKey(), (store) =>
        store?.mode === 'items'
          ? { ...store, items: applyToItems(store.items, result, variables) }
          : store
      );
      trackEvent('CHAT_SETTING_PROMPT_UPDATED');
    },
    onError: (error, _, senderId) => {
      if (senderId !== userStore.userId) return;
      reportNoteError(error, failureMessage);
      // The list then shows what the server holds: a note that changed, a note that is gone,
      // or a store kept as text. An open edit form keeps its typed text, since the list holds it
      void queryClient.invalidateQueries({ queryKey: notesStoreQueryKey() });
    },
  });
};

export const useAddNoteMutation = () =>
  useNoteWriteMutation(
    async (text: string) =>
      NoteItemSchema.parse(
        await apiClient.post(ITEMS_URL, { text }, QUIET_NO_RETRIES)
      ),
    (items, added) => [...items, added],
    "Couldn't add this note. Try again."
  );

export const useSaveNoteMutation = () =>
  useNoteWriteMutation(
    async ({ number, text, version }: NoteWriteVariables) =>
      NoteItemSchema.parse(
        await apiClient.put(
          `${ITEMS_URL}/${number}`,
          { text, version },
          QUIET_NO_RETRIES
        )
      ),
    (items, saved) =>
      items.map((item) => (item.number === saved.number ? saved : item)),
    "Couldn't save this note. Try again."
  );

export const useDeleteNoteMutation = () =>
  useNoteWriteMutation(
    ({ number, version }: Omit<NoteWriteVariables, 'text'>) =>
      apiClient.delete(
        `${ITEMS_URL}/${number}?version=${version}`,
        QUIET_NO_RETRIES
      ),
    (items, _, { number }) => items.filter((item) => item.number !== number),
    "Couldn't delete this note. Try again."
  );
